<template>
  <div class="card-wrapper eta-card" @click.prevent="onSendAnalytics">
    <div class="circle-wrapper">
      <BmCircle>
        <ProgressCircle
          :isGlsTenant="$rttI18n.isGlsTenant"
          :isPreEta="trackingDataStore.isJobDeliveryStatusPreEta"
          :progress="progress"
          :variant="progressCircleVariant"
          ariaLabelledById="eta-card-title"
        />
        <div class="card-content">
          <template v-if="trackingDataStore.isJobInProgress">
            <template v-if="isRouteJumpActive">
              <div class="lottie-player">
                <div class="absolute-container">
                  <Vue3Lottie
                    :animationLink="
                      publicPath('/animations/map_recalculating.json')
                    "
                  />
                </div>
              </div>
            </template>
            <template v-else-if="shouldShowLastStopAnimation">
              <div class="lottie-player">
                <div class="absolute-container">
                  <Vue3Lottie
                    :animationLink="
                      publicPath('/animations/zero_stops_box.json')
                    "
                    :loop="isAnimationActive"
                    :pauseAnimation="!isAnimationActive"
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <h3 id="eta-card-title" class="card-title">{{ title }}</h3>
              <div class="card-subtitle" v-html="safeSubtitle" />
            </template>
          </template>
          <template v-else-if="trackingDataStore.isJobFinished">
            <img
              :alt="jobFinishedImage.alt"
              :class="[
                'card-image',
                (trackingDataStore.isServiceTypePickup ||
                  trackingDataStore.isServiceTypeCollection) &&
                  'pickup'
              ]"
              :src="jobFinishedImage.src"
            />
          </template>
        </div>
      </BmCircle>
    </div>
  </div>
</template>

<script setup>
import { sendAnalytics } from '@/services/analytics.js';
import BmCircle from '@/v2/design-system/components/Circle/BmCircle.vue';
import ProgressCircle from '@/v2/components/tracking/ProgressCircle.vue';
import { computed, inject, watchEffect } from 'vue';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useRouteJumpHandler } from '@/v2/components/map/useRouteJumpHandler.js';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import publicPath from '@/util/publicPath.js';
import { Vue3Lottie } from 'vue3-lottie';
import parcelSuccessImage from '../assets/parcel_success.png';
import parcelFailImage from '../assets/parcel_fail.png';
import pickupSuccessImage from '../assets/pickup_success.png';
import pickupFailImage from '../assets/pickup_fail.png';
import { getStopsStep } from '@/util/get.js';
import { useSeasonalTheme } from '@/v2/composables/useSeasonalTheme.js';
import { useAnimation } from '@/v2/composables/useAnimation.js';

const props = defineProps({
  showDetails: { type: Boolean, required: false, default: false }
});

const $t = inject('t');
const $rttI18n = inject('rttI18n');
const $rttMoment = inject('rttMoment');
const trackingDataStore = useTrackingDataStore();
const { isActive: isRouteJumpActive } = useRouteJumpHandler({
  silent: true
});

const { getSeasonalAsset } = useSeasonalTheme();
const { isAnimationActive, startTimedAnimation } = useAnimation();

const remainingStops = computed(() => {
  return trackingDataStore.data.status.remainingStops;
});

const shouldShowLastStopAnimation = computed(() => {
  const minutesLeft = trackingDataStore.getMinutesLeft;
  const remainingMinutes = minutesLeft.from;

  return (
    trackingDataStore.isJobInProgress &&
    remainingStops.value === 0 &&
    minutesLeft.format === 'min' &&
    typeof remainingMinutes === 'number' &&
    remainingMinutes <= 30
  );
});

watchEffect(() => {
  if (shouldShowLastStopAnimation.value) {
    startTimedAnimation();
  }
});

const displayStops = computed(() => {
  return trackingDataStore.isJobStatusShadowed
    ? getStopsStep(remainingStops.value)
    : remainingStops.value;
});

const safeSubtitle = computed(() => {
  if (
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return trackingDataStore.isServiceTypePickup ||
      trackingDataStore.isServiceTypeCollection
      ? DOMPurify.sanitize($t('etaCard.subtitle.pickupBlocked'))
      : DOMPurify.sanitize($t('etaCard.subtitle.blocked'));
  }
  return DOMPurify.sanitize($t('etaCard.subtitle.eta'));
});

const title = computed(() => {
  const showDetails = props.showDetails;
  const minutesLeft = trackingDataStore.getMinutesLeft;

  const etaTimeframeText =
    `${moment(trackingDataStore.getTimeframe.from).format($rttMoment.timeFormat)}-` +
    `${moment(trackingDataStore.getTimeframe.to).format($rttMoment.timeFormat)}`;

  const translatedTimeFormatAbbreviation =
    minutesLeft.format === 'min'
      ? $t('etaCard.title.minuteAbbreviation')
      : $t('etaCard.title.hourAbbreviation');

  const etaMinutesRangeText =
    `${minutesLeft.from}-` +
    `${minutesLeft.to} ${translatedTimeFormatAbbreviation}`;

  if (trackingDataStore.isJobDeliveryStatusPreEta) {
    return etaTimeframeText;
  }

  if (
    displayStops.value === null ||
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return $t('etaCard.title.blocked');
  }

  if (
    !showDetails ||
    !minutesLeft.from ||
    trackingDataStore.isJobStatusShadowed ||
    minutesLeft.format !== 'min' ||
    minutesLeft.to >= 60
  ) {
    return etaTimeframeText;
  }
  if (displayStops.value >= 0) {
    return etaMinutesRangeText;
  }
  return '';
});

const progress = computed(() => {
  const showDetails = props.showDetails;

  if (
    !showDetails ||
    displayStops.value === null ||
    displayStops.value > 50 ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return 0;
  }
  if (displayStops.value > 30) {
    return 10;
  }
  if (displayStops.value > 10) {
    return 38;
  }
  if (displayStops.value > 6) {
    return 75;
  }
  if (displayStops.value > 1) {
    return 87;
  }
  if (displayStops.value >= 0) {
    return 93;
  }
  return 0;
});

const jobFinishedImage = computed(() => {
  if (trackingDataStore.isJobStatusDelivered) {
    return {
      alt: $t('trackingFinished.delivered.heading'),
      src: getSeasonalAsset('parcelSuccess') || parcelSuccessImage
    };
  }

  if (trackingDataStore.isJobStatusNotDelivered) {
    return {
      alt: $t('trackingFinished.notDelivered.heading'),
      src: parcelFailImage
    };
  }

  if (
    trackingDataStore.isJobStatusPickedUp ||
    trackingDataStore.isJobStatusCollected
  ) {
    return {
      alt: $t('trackingFinished.pickedUp.heading'),
      src: pickupSuccessImage
    };
  }

  if (
    trackingDataStore.isJobStatusNotPickedUp ||
    trackingDataStore.isJobStatusNotCollected
  ) {
    return {
      alt: $t('trackingFinished.notPickedUp.heading'),
      src: pickupFailImage
    };
  }

  return { alt: '', src: '' };
});

const progressCircleVariant = computed(() => {
  if (!props.showDetails) return 'base';
  if (isRouteJumpActive.value) return 'segmented';
  if (trackingDataStore.isJobDeliveryStatusPreEta) return 'segmented';
  return 'progress';
});

const onSendAnalytics = () => {
  sendAnalytics(trackingDataStore.data, 'clickStats', 'circle_card_clicked', {
    isV2: true
  });
};
</script>

<style scoped>
@media (width < 480px) {
  .card-wrapper {
    width: 100%;
    padding: 0;
    border: 0;
  }
}

.eta-card {
  position: relative;
  margin-bottom: -108px;
  z-index: 3 !important;
}

.circle-wrapper {
  width: 250px;
  height: 250px;
  position: relative;
  left: 50%;
  transform: translate(-50%, 0%);
}

.card-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--pad-space-sp-48) var(--pad-space-sp-32);
}

.card-title {
  color: var(--neutral-text-icon-title);
  font-family: var(--header-h-3-bold-family), sans-serif;
  font-size: var(--header-h-3-bold-size);
  font-style: normal;
  font-weight: var(--header-h-3-bold-weight);
  line-height: var(--header-h-3-bold-line-height);
  text-align: center;
}

.card-subtitle {
  color: var(--neutral-text-icon-label);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;

  font-family: var(--body-small-med-family);
  font-size: var(--body-small-med-size);
  font-style: normal;
  font-weight: var(--body-small-med-weight);
  line-height: var(--line-height-lead-sm);
}

.lottie-player {
  margin: 0;
  position: relative;
  overflow: visible;
  pointer-events: none;
  width: 100%;
  height: 100%;

  & > .absolute-container {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
  }
}

.card-image {
  height: 78px;

  &.pickup {
    height: 100px;
  }
}
</style>
